import * as AvatarPrimitive from "@radix-ui/react-avatar";
import * as React from "react";
import { cx } from "../internal/cx.js";

export type AvatarSize = "sm" | "md" | "lg" | "xl";

export interface AvatarProps extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> {
  size?: AvatarSize;
}

/**
 * Avatar root — circular container that shows the first child that
 * succeeds: `<AvatarImage>` if it loads, otherwise `<AvatarFallback>`.
 * Always provide a fallback (typically initials) so the avatar renders
 * a non-empty state while the image is loading or absent.
 */
export const Avatar = React.forwardRef<React.ElementRef<typeof AvatarPrimitive.Root>, AvatarProps>(
  function Avatar({ className, size = "md", ...props }, ref) {
    return (
      <AvatarPrimitive.Root
        ref={ref}
        className={cx("cw-v2-avatar", `cw-v2-avatar--${size}`, className)}
        {...props}
      />
    );
  },
);
Avatar.displayName = "Avatar";

export const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(function AvatarImage({ className, ...props }, ref) {
  return (
    <AvatarPrimitive.Image ref={ref} className={cx("cw-v2-avatar__image", className)} {...props} />
  );
});
AvatarImage.displayName = "AvatarImage";

export const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(function AvatarFallback({ className, ...props }, ref) {
  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      className={cx("cw-v2-avatar__fallback", className)}
      {...props}
    />
  );
});
AvatarFallback.displayName = "AvatarFallback";

/** One member rendered inside an `<AvatarGroup>`. */
export interface AvatarGroupItem {
  /** Stable key. */
  id: string;
  /** Fallback text (initials or short abbreviation). Required so the avatar
   * always renders a non-empty state. */
  fallback: React.ReactNode;
  /** Optional image source; falls back to `fallback` on load failure. */
  src?: string;
  /** Accessible name for the avatar / image alt. */
  name?: string;
}

export interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Members to display, in order. */
  items: AvatarGroupItem[];
  /** Avatar size for every member and the overflow chip. Default `"sm"`. */
  size?: AvatarSize;
  /**
   * Cap on visible avatars. When `items.length` exceeds `max`, the last slot
   * becomes a "+N" overflow chip. Omit or pass `0` to show all. Default `4`.
   */
  max?: number;
  /** Accessible label for the group. Default derived from the member count. */
  ariaLabel?: string;
}

/**
 * A horizontal cluster of overlapping avatars with a "+N" overflow chip.
 * Built on `Avatar` / `AvatarFallback`; use for rosters, covered-members
 * rows, and any "who is on this" summary. Purely presentational.
 */
export const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(function AvatarGroup(
  { ariaLabel, className, items, max = 4, size = "sm", ...props },
  ref,
) {
  const limit = max > 0 ? max : items.length;
  // When there is an overflow, reserve one slot for the "+N" chip so the total
  // rendered count never exceeds `max`.
  const hasOverflow = items.length > limit;
  const visible = hasOverflow ? items.slice(0, limit - 1) : items;
  const overflowCount = items.length - visible.length;

  return (
    <div
      ref={ref}
      className={cx("cw-v2-avatar-group", className)}
      aria-label={ariaLabel ?? `${items.length} ${items.length === 1 ? "person" : "people"}`}
      // biome-ignore lint/a11y/useSemanticElements: an avatar cluster is a
      // labelled grouping of related people, not a form control — role="group"
      // is the correct ARIA role; <fieldset> would be semantically wrong here.
      role="group"
      {...props}
    >
      {visible.map((item) => (
        <Avatar key={item.id} size={size} className="cw-v2-avatar-group__item" title={item.name}>
          {item.src ? <AvatarImage alt={item.name ?? ""} src={item.src} /> : null}
          <AvatarFallback>{item.fallback}</AvatarFallback>
        </Avatar>
      ))}
      {overflowCount > 0 ? (
        <Avatar
          size={size}
          className="cw-v2-avatar-group__item cw-v2-avatar-group__overflow"
          aria-hidden
        >
          <AvatarFallback>{`+${overflowCount}`}</AvatarFallback>
        </Avatar>
      ) : null}
    </div>
  );
});
AvatarGroup.displayName = "AvatarGroup";
